const UPLOAD_URL = '/api/v1/documents/actions/upload';
const DEFAULT_BASE_URL = 'https://www.zillowdocs.com';

export enum FileUploadStatus {
  Rejected = 'Rejected',
  Pending = 'Pending',
  Uploading = 'Uploading',
  Completed = 'Completed',
  Failed = 'Failed',
}

export enum ErrorType {
  ClientError = 'Client Error',
  ServerError = 'Server Error',
}

export enum ErrorCode {
  Client = 'exception.client',
  Server = 'exception.server',
  Upload = 'exception.upload',
  EncryptedPdf = 'exception.pdf.encrypted',
}

export interface ZDocsUpload {
  file: File;
  processingGroupId: number;
  progress: number;
  status: FileUploadStatus;
  errorType: ErrorType;
  messageToken: string | null;
  errorMessage: string | null;
  retryCount: number;
  statusCode: number;
  errorCode: string | null;
}

interface PendingPromiseState {
  resolve: (value: Array<ZDocsUpload>) => void;
  uploads: Array<ZDocsUpload>;
}

type UploadsByStatus = {
  pending: Array<ZDocsUpload>;
  uploading: Array<ZDocsUpload>;
  completed: Array<ZDocsUpload>;
  failed: Array<ZDocsUpload>;
};

type UploadStatus = {
  fileName?: string;
  messageToken?: string;
  message?: string;
  code?: string;
};

type UploadServerErrors = Array<{ errorMessage: string }>;

type UploadFileErrors = Array<UploadStatus>;

type UploadResponse = {
  success?: Array<UploadStatus>;
  failed?: UploadFileErrors;
  errors?: UploadServerErrors;
};

type UploadError = {
  errorType: ErrorType;
  errorCode: ErrorCode | string;
  errorMessage: string;
};

type UploadDataPatch = {
  progress?: number;
  status?: FileUploadStatus;
  errorType?: ErrorType;
  messageToken?: string | null;
  errorMessage?: string | null;
  statusCode?: number;
  retryCount?: number;
  errorCode?: string | null;
};

export type ZDocsUploadManagerConsumableOptions = {
  /** Call your backend API to request a userToken from zDocs here  */
  generateUploadToken: () => Promise<string>;
  /** Callback when uploads are in progress */
  onUploadActive?: (isUploadActive: boolean) => void;
  /** Callback when a single upload has finished */
  onUploadComplete?: (upload: ZDocsUpload) => void;
  /** Callback when a single upload has failed */
  onUploadFailed?: (upload: ZDocsUpload) => void;
  /** Changes the base url to call zdocs. Defaults to https://www.zillowdocs.com. */
  baseUrl?: string;
  /** Maximum number of times to automatically retry failed uploads */
  maxRetries?: number;
};

export type Options = {
  onChange?: (
    changedFiles: Array<ZDocsUpload>,
    allFiles: Array<ZDocsUpload>,
    processingGroupId: number
  ) => void;
  onProgress?: (upload: ZDocsUpload) => void;
  queueSize?: number;
} & ZDocsUploadManagerConsumableOptions;

export default class ZDocsUploadManager {
  // Options //
  generateUploadToken: () => Promise<string>;
  onChange?: (
    changedFiles: Array<ZDocsUpload>,
    allFiles: Array<ZDocsUpload>,
    processingGroupId: number
  ) => void;
  onProgress?: (upload: ZDocsUpload) => void;
  onUploadActive?: (isActive: boolean) => void;
  onUploadComplete?: (upload: ZDocsUpload) => void;
  onUploadFailed?: (upload: ZDocsUpload) => void;
  queueSize: number;
  maxRetries: number;
  baseUrl: string;

  // State //
  uploads: Array<ZDocsUpload>;
  uploadToken: string | null = null;
  uploadTokenPromise?: Promise<string>;
  uploadTokenPromisePending = false;
  pendingPromiseStates: Array<PendingPromiseState>;
  isUploadActive: boolean;
  notifiedComplete: Array<ZDocsUpload>;
  notifiedFailed: Array<ZDocsUpload>;
  isProcessingState: boolean;
  retryTimeoutId?: number;
  processingGroupId = -1;

  constructor({
    generateUploadToken,
    onChange,
    onProgress,
    onUploadActive,
    onUploadComplete,
    onUploadFailed,
    queueSize = 1,
    maxRetries = 0,
    baseUrl = DEFAULT_BASE_URL,
  }: Options) {
    this.generateUploadToken = generateUploadToken;
    this.onChange = onChange;
    this.onProgress = onProgress;
    this.onUploadActive = onUploadActive;
    this.onUploadComplete = onUploadComplete;
    this.onUploadFailed = onUploadFailed;
    this.queueSize = queueSize;
    this.maxRetries = maxRetries;
    this.baseUrl = baseUrl;

    this.uploads = [];
    this.pendingPromiseStates = [];
    this.isUploadActive = false;
    this.notifiedComplete = [];
    this.notifiedFailed = [];
    this.isProcessingState = false;
  }

  async uploadFiles(
    files: File | FileList | Array<File>
  ): Promise<Array<ZDocsUpload>> {
    const groupId = ++this.processingGroupId;
    const uploadingGroup = [] as Array<ZDocsUpload>;
    const retryGroup = [] as Array<ZDocsUpload>;
    const newFiles =
      files instanceof FileList
        ? Array.from(files)
        : Array.isArray(files)
        ? files
        : [files];

    this.uploads.forEach(upload => {
      // Check for files that were previously uploaded. Failed ones can be retried.
      const fileIndex = newFiles.indexOf(upload.file);
      const isExistingFile = fileIndex >= 0;
      const isRetry =
        isExistingFile && upload.status === FileUploadStatus.Failed;
      const isUploading =
        isRetry ||
        upload.status === FileUploadStatus.Pending ||
        upload.status === FileUploadStatus.Uploading;

      if (isExistingFile) {
        newFiles.splice(fileIndex, 1);
      }

      if (isUploading) {
        // Update all uploads in the group to the new group id
        upload.processingGroupId = groupId;
        uploadingGroup.push(upload);
      }

      if (isRetry) {
        upload.status = FileUploadStatus.Pending;
        upload.progress = 0;
        upload.errorMessage = null;
        upload.errorCode = null;
        upload.retryCount = 0;

        retryGroup.push(upload);

        // Removed the failure notification so it can be redelivered
        const index = this.notifiedFailed.indexOf(upload);
        if (index >= 0) {
          this.notifiedFailed.splice(index, 1);
        }
      }
    });

    // Any remaining files are new.
    const newUploads = newFiles.map(file => {
      return {
        file,
        processingGroupId: groupId,
        progress: 0,
        status: FileUploadStatus.Pending,
        retryCount: 0,
      } as ZDocsUpload;
    });

    // Add to the list
    this.uploads = [...this.uploads, ...newUploads];

    // Notify of file changes
    this._onChange([...uploadingGroup, ...newUploads]);

    // Return a promise for the specific uploads
    const promise = new Promise<Array<ZDocsUpload>>(resolve => {
      this.pendingPromiseStates.push({
        resolve,
        uploads: [...retryGroup, ...newUploads],
      });
    });

    // Kick off the uploads
    this._processUploadState();

    return promise;
  }

  deleteFile(file: File) {
    const index = this.uploads.findIndex(upload => upload.file === file);

    if (index === -1) {
      return;
    }

    const { status } = this.uploads[index];
    if (
      status !== FileUploadStatus.Failed &&
      status !== FileUploadStatus.Rejected
    ) {
      return;
    }

    const deletedFiles = this.uploads.splice(index, 1);
    this._onChange(deletedFiles);
  }

  disconnect() {
    this._cancelRetry();
    this.onChange = undefined;
    this.onProgress = undefined;
    this.onUploadActive = undefined;
    this.onUploadComplete = undefined;
    this.onUploadFailed = undefined;
    this.generateUploadToken = () => Promise.resolve('');
  }

  private _getUploadToken(): Promise<string> {
    if (this.uploadToken) {
      this.uploadTokenPromise = Promise.resolve(this.uploadToken);
      this.uploadTokenPromisePending = false;
    } else if (!this.uploadTokenPromise || !this.uploadTokenPromisePending) {
      this.uploadTokenPromisePending = true;
      this.uploadTokenPromise = this.generateUploadToken()
        .then(uploadToken => {
          this.uploadToken = uploadToken;
          return uploadToken;
        })
        .finally(() => {
          this.uploadTokenPromisePending = false;
        });
    }

    return this.uploadTokenPromise;
  }

  private async _startUpload(pendingUpload: ZDocsUpload) {
    const file = pendingUpload.file;

    this._updateFileData(file, {
      progress: 0,
      status: FileUploadStatus.Uploading,
    });

    let uploadToken: string;
    try {
      uploadToken = await this._getUploadToken();

      if (!uploadToken) {
        throw Error('No Upload Token');
      }

      const formData = new FormData();
      formData.set('file', file);

      const fileRequest = new XMLHttpRequest();
      fileRequest.open('POST', `${this.baseUrl}${UPLOAD_URL}`);
      fileRequest.setRequestHeader('Accept', 'application/json');
      fileRequest.setRequestHeader('Authorization', `Bearer ${uploadToken}`);
      fileRequest.setRequestHeader(
        'x-zdocs-sdk-web',
        `${import.meta.env.VITE_SHA}`
      );

      fileRequest.onload = async () => {
        this._handleUploadResult(file, fileRequest);
      };

      fileRequest.onerror = _e => {
        const clientError = this._getClientError(fileRequest.responseText);
        this._handleUploadError(file, clientError);
      };

      fileRequest.upload.onprogress = e => {
        if (e.lengthComputable) {
          const upload = this._getUpload(file);
          if (upload) {
            const progress = Math.ceil((e.loaded / e.total) * 100);
            this._updateFileData(file, { progress });
            this._onProgress(upload);
          }
        }
      };

      fileRequest.send(formData);
    } catch (e) {
      this.uploadToken = null;
      const initializationError = this._getInitializationError(e);
      this._handleUploadError(file, initializationError);
    }
  }

  private _cancelRetry() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    this.retryTimeoutId = undefined;
  }

  private _handleUploadResult(file: File, fileRequest: XMLHttpRequest) {
    const { responseText, status } = fileRequest;
    const { success, errors, failed } =
      this._getUploadResponseJson(responseText) ?? {};

    if (status !== 200 || success?.length !== 1) {
      const uploadError = this._getUploadError(status, errors, failed);

      if (status === 401 || status === 403) {
        // Clear token so we try to get a new one.
        this.uploadToken = null;
      }

      this._handleUploadError(file, uploadError, fileRequest.status);
      return;
    }

    const { messageToken } = success[0];

    this._updateFileData(file, {
      status: FileUploadStatus.Completed,
      progress: 100,
      messageToken: messageToken || null,
    });
  }

  private _getUploadResponseJson(
    responseText: string
  ): UploadResponse | undefined {
    try {
      return JSON.parse(responseText);
    } catch (e) {}

    return undefined;
  }

  private _getClientError(responseText: string) {
    return {
      errorType: ErrorType.ClientError,
      errorCode: ErrorCode.Client,
      errorMessage: responseText || 'Unknown Client Error',
    };
  }

  private _getInitializationError(error: unknown): UploadError {
    const errorMessage =
      error instanceof Error && error.message
        ? error.message
        : 'Unknown Initialization Error';

    return {
      errorType: ErrorType.ServerError,
      errorCode: ErrorCode.Server,
      errorMessage,
    };
  }

  private _getUploadFileError(
    fileErrors: UploadFileErrors | undefined
  ): UploadError {
    const { message, code } = fileErrors?.[0] ?? {};
    const errorCode = code || ErrorCode.Upload;
    const errorMessage = message || 'Unsuccessful Upload';

    return {
      errorType: ErrorType.ServerError,
      errorCode,
      errorMessage,
    };
  }

  private _getDefaultServerErrorMessage(statusCode: number) {
    switch (statusCode) {
      case 0:
        return 'No Response';
      case 400:
        return 'Bad Request';
      case 401:
        return 'Unauthorized';
      case 403:
        return 'Forbidden';
      case 408:
        return 'Request Timeout';
      case 500:
        return 'Server Error';
    }

    return 'Unhandled Response';
  }

  private _getUploadServerError(
    statusCode: number,
    serverErrors: UploadServerErrors | undefined
  ): UploadError {
    const { errorMessage } = serverErrors?.[0] ?? {};

    return {
      errorType: ErrorType.ServerError,
      errorCode: ErrorCode.Upload,
      errorMessage:
        errorMessage || this._getDefaultServerErrorMessage(statusCode),
    };
  }

  private _getUploadError(
    statusCode: number,
    serverErrors: UploadServerErrors | undefined,
    fileErrors: UploadFileErrors | undefined
  ): UploadError {
    if (statusCode === 200) {
      return this._getUploadFileError(fileErrors);
    }

    return this._getUploadServerError(statusCode, serverErrors);
  }

  private _handleUploadError(
    file: File,
    uploadError: UploadError,
    requestStatus?: number
  ) {
    const upload = this._getUpload(file);
    if (!upload) {
      return;
    }

    const fileData = this._getUploadFailureFileData(
      upload,
      uploadError,
      requestStatus
    );

    if (fileData.status === FileUploadStatus.Pending) {
      this._cancelRetry();

      this.retryTimeoutId = setTimeout(() => {
        this.retryTimeoutId = undefined;
        this._updateFileData(file, fileData);
      }, 1000);

      return;
    }

    this._updateFileData(file, fileData);
  }

  private _getUploadFailureStatus(upload: ZDocsUpload, errorCode: string) {
    if (errorCode === ErrorCode.EncryptedPdf) {
      return FileUploadStatus.Rejected;
    }

    if (upload.retryCount < this.maxRetries) {
      return FileUploadStatus.Pending;
    }

    return FileUploadStatus.Failed;
  }

  private _getUploadFailureFileData(
    upload: ZDocsUpload,
    uploadError: UploadError,
    requestStatus?: number
  ): UploadDataPatch {
    const { retryCount } = upload;
    const { errorCode, errorMessage } = uploadError;
    const status = this._getUploadFailureStatus(upload, errorCode);

    if (status === FileUploadStatus.Pending) {
      return {
        status,
        progress: 0,
        messageToken: null,
        errorMessage,
        retryCount: retryCount + 1,
      };
    }

    return {
      ...uploadError,
      status,
      progress: 100,
      statusCode: requestStatus,
      messageToken: null,
    };
  }

  private _getUpload(file: File) {
    return this.uploads.find(upload => upload.file === file);
  }

  private _onProgress(upload: ZDocsUpload) {
    if (this.onProgress) {
      this.onProgress(upload);
    }
  }

  private _updateFileData(
    file: File,
    {
      progress,
      status,
      messageToken,
      errorType,
      errorMessage,
      statusCode,
      retryCount,
      errorCode,
    }: UploadDataPatch
  ) {
    const upload = this._getUpload(file);
    if (!upload) {
      return;
    }

    if (progress !== undefined) {
      upload.progress = progress;
    }
    if (status !== undefined) {
      upload.status = status;
    }
    if (messageToken !== undefined) {
      upload.messageToken = messageToken;
    }
    if (errorType !== undefined) {
      upload.errorType = errorType;
    }
    if (errorMessage !== undefined) {
      upload.errorMessage = errorMessage;
    }
    if (errorCode !== undefined) {
      upload.errorCode = errorCode;
    }
    if (statusCode !== undefined) {
      upload.statusCode = statusCode;
    }
    if (retryCount !== undefined) {
      upload.retryCount = retryCount;
    }

    // Notify of changes
    this._onChange([upload]);

    // Always re-evaluate the upload state
    this._processUploadState();
  }

  private _onChange(changedUploads: Array<ZDocsUpload>) {
    if (this.onChange) {
      this.onChange(changedUploads, [...this.uploads], this.processingGroupId);
    }
  }

  private _processUploadState() {
    if (!this.isProcessingState) {
      this.isProcessingState = true;
      this._doProcessUploadState();
      this.isProcessingState = false;
    }
  }

  private _doProcessUploadState() {
    const { pending, uploading, completed, failed } = this._uploadsByStatus();

    // Check for `isUploadActive` change
    const stateIsUploading = pending.length > 0 || uploading.length > 0;
    if (stateIsUploading !== this.isUploadActive) {
      this.isUploadActive = stateIsUploading;
      if (this.onUploadActive) {
        this.onUploadActive(this.isUploadActive);
      }
    }

    // Check for any new completions
    completed.forEach(upload => {
      if (!this.notifiedComplete.includes(upload)) {
        this.notifiedComplete.push(upload);
        if (this.onUploadComplete) {
          this.onUploadComplete(upload);
        }
      }
    });

    // Check for any new failures
    failed.forEach(upload => {
      if (!this.notifiedFailed.includes(upload)) {
        this.notifiedFailed.push(upload);
        if (this.onUploadFailed) {
          this.onUploadFailed(upload);
        }
      }
    });

    // Check if any pending promises need resolved
    for (let i = 0; i < this.pendingPromiseStates.length; i++) {
      const pendingPromiseState = this.pendingPromiseStates[i];
      const isReadyToResolve = pendingPromiseState.uploads.every(
        upload =>
          upload.status === FileUploadStatus.Completed ||
          upload.status === FileUploadStatus.Failed ||
          upload.status === FileUploadStatus.Rejected
      );

      if (isReadyToResolve) {
        pendingPromiseState.resolve(pendingPromiseState.uploads);
        this.pendingPromiseStates.splice(i, 1);
        i--;
      }
    }

    // If the number of uploads are below the queue size, try to start more.
    if (uploading.length < this.queueSize) {
      const queueCapacity = this.queueSize - uploading.length;
      for (let i = 0; i < pending.length && i < queueCapacity; i++) {
        this._startUpload(pending[i]);
      }
    }
  }

  private _uploadsByStatus(): UploadsByStatus {
    const uploadsByStatus = this.uploads.reduce(
      (result: UploadsByStatus, upload) => {
        if (upload.status === FileUploadStatus.Pending) {
          result.pending.push(upload);
        } else if (upload.status === FileUploadStatus.Uploading) {
          result.uploading.push(upload);
        } else if (upload.status === FileUploadStatus.Completed) {
          result.completed.push(upload);
        } else if (
          upload.status === FileUploadStatus.Failed ||
          upload.status === FileUploadStatus.Rejected
        ) {
          result.failed.push(upload);
        }
        return result;
      },
      { pending: [], uploading: [], completed: [], failed: [] }
    );

    // Sort the pending uploads by retryCount, so fresh uploads get priority.
    uploadsByStatus.pending.sort((a, b) => a.retryCount - b.retryCount);

    return uploadsByStatus;
  }
}
